import { Toaster as SonnerToaster, type ToasterProps } from 'sonner';

/**
 * shadcn's own `sonner.tsx` component — the toast primitive shadcn/ui now
 * generates by default (replacing the Radix `Toast`-based component this
 * project briefly had). Mounted once in `app/app.tsx`, outside `<Template />`,
 * so every one of the ten templates shares one `<Toaster />` and every
 * `toast(...)` call anywhere in the app — regardless of which component tree
 * it's called from — renders into it.
 *
 * `theme="system"` rather than plumbing through `next-themes` (which this
 * project doesn't use): sonner's own `system` mode watches
 * `prefers-color-scheme` itself, no extra dependency needed. The
 * `--normal-*` CSS variables map to the app's existing shadcn design tokens
 * (`--popover`/`--popover-foreground`/`--border`, already themed for dark
 * mode in `index.css`), so a toast is styled consistently with the rest of
 * the shadcn components rather than sonner's own default palette.
 * `richColors` turns on sonner's built-in semantic palette for
 * `toast.error`/`toast.success`, rather than hand-rolling `variant` props the
 * way the Radix-based `Toast` this replaces did.
 *
 * `offset`/`mobileOffset` account for `--safe-area-inset` (installed-PWA
 * status bar / home indicator) — a bare pixel offset would put a toast
 * partly under the OS chrome on notched devices, the same class of bug
 * topic 20 tracks for hardcoded insets elsewhere in the app.
 */
export function Toaster(props: ToasterProps) {
	return (
		<SonnerToaster
			theme="system"
			richColors
			className="toaster group"
			offset="calc(var(--safe-area-inset, 0px) + 16px)"
			mobileOffset="calc(var(--safe-area-inset, 0px) + 16px)"
			style={
				{
					'--normal-bg': 'var(--popover)',
					'--normal-text': 'var(--popover-foreground)',
					'--normal-border': 'var(--border)',
				} as React.CSSProperties
			}
			{...props}
		/>
	);
}
